/**
 * Datentypen und reine Helfer der Stellplatz-Erfassung (xlsx Z. 7, Punkt 7).
 *
 * Bewusst **ohne** React: die Vertragsmaske (`immobilieContractStep`), die
 * Zusammenfassung des Step-Formulars und die Risiko-Detailseite lesen dieselben
 * Werte, brauchen davon aber nur das Parsen und das Formatieren. Lägen die
 * Helfer weiter in `StellplatzBlock.tsx`, zöge jeder dieser Aufrufer die
 * komplette Komponente samt ihres Griffel-`makeStyles` mit ins Bundle.
 * Vorbild ist `beteiligteTypes.ts`.
 *
 * Die Auswahl bleibt in `stellplatzArten` (Array der Beschriftungen) — dasselbe
 * Format wie bisher, damit Bestandsdaten und die Vertragsmaske weiter passen.
 * Die Details je Art liegen unter `stellplatzDetails`:
 *
 *   { tiefgarage: { lage: "im Gebäude", lageWo: "Untergeschoss", anzahl: "12" },
 *     garage:     { lage: "außerhalb", anzahl: "2" } }
 */
import {
  STELLPLATZ_ARTEN_DEF,
  STELLPLATZ_LAGE_INNEN,
  type StellplatzArtKey,
} from "./enums";
import { asArray, asRecordOfRecords } from "./structuredAnswers";

export type StellplatzDetail = {
  lage?: string;
  /**
   * Früherer Freitext „wo genau im Gebäude?". Seit 10/2026 abgelöst durch
   * `flaeche` und `geschoss`; bleibt lesbar, wird aber nicht mehr neu erfragt.
   */
  lageWo?: string;
  anzahl?: string;
  /** Nur bei `lage === "im Gebäude"`: Fläche der Garage in m². */
  flaeche?: string;
  /** Nur bei `lage === "im Gebäude"`: Geschoss, z. B. „UG 1" oder „EG". */
  geschoss?: string;
  /**
   * Nur Tiefgarage im Gebäude: liegt sie allein unter dem Gebäude oder reicht
   * sie darüber hinaus (Fachabstimmung 31.08.2026)? Werte aus
   * `TIEFGARAGE_AUSDEHNUNG`.
   */
  ausdehnung?: string;
  /** Nur bei Ausdehnung „darüber hinaus": Fläche außerhalb des Gebäudegrundrisses in m². */
  flaecheAusserhalb?: string;
};

export const TIEFGARAGE_NUR_UNTER = "nur unter dem Gebäude";
export const TIEFGARAGE_DARUEBER_HINAUS = "unter dem Gebäude und darüber hinaus";
export const TIEFGARAGE_AUSDEHNUNG = [TIEFGARAGE_NUR_UNTER, TIEFGARAGE_DARUEBER_HINAUS] as const;

export type StellplatzDetails = Partial<Record<StellplatzArtKey, StellplatzDetail>>;

export const STELLPLATZ_ART_KEYS: StellplatzArtKey[] = STELLPLATZ_ARTEN_DEF.map(
  (a) => a.key,
);

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v !== "" ? v : undefined;

/** Liest die Auswahl aus einem Answer-Wert; alles Nicht-Textliche fällt heraus. */
export const asStellplatzArten = (value: unknown): string[] =>
  asArray<unknown>(value).filter((v): v is string => typeof v === "string");

/**
 * Liest `stellplatzDetails` aus einem Answer-Wert. Unbekannte Schlüssel und
 * Nicht-Objekte fallen heraus — auch das Korruptionsmuster `{0:"{",1:"\""}`
 * eines versehentlich gespreadeten JSON-Strings. Idempotent.
 */
export const asStellplatzDetails = (value: unknown): StellplatzDetails => {
  const roh = asRecordOfRecords(value);
  const out: StellplatzDetails = {};
  for (const key of STELLPLATZ_ART_KEYS) {
    const eintrag = roh[key];
    if (!eintrag) continue;
    const detail: StellplatzDetail = {};
    const lage = str(eintrag.lage);
    const lageWo = str(eintrag.lageWo);
    const anzahl = str(eintrag.anzahl);
    const flaeche = str(eintrag.flaeche);
    const geschoss = str(eintrag.geschoss);
    const ausdehnung = str(eintrag.ausdehnung);
    const flaecheAusserhalb = str(eintrag.flaecheAusserhalb);
    if (lage) detail.lage = lage;
    if (lageWo) detail.lageWo = lageWo;
    if (anzahl) detail.anzahl = anzahl;
    if (flaeche) detail.flaeche = flaeche;
    if (geschoss) detail.geschoss = geschoss;
    if (ausdehnung) detail.ausdehnung = ausdehnung;
    if (flaecheAusserhalb) detail.flaecheAusserhalb = flaecheAusserhalb;
    out[key] = detail;
  }
  return out;
};

/**
 * Geschoss-Auswahl für eine Garage im Gebäude, aus den erfassten Geschossen
 * abgeleitet: „UG 2", „UG 1", „EG", „OG 1" … Ohne Angaben mindestens UG 1,
 * EG und OG 1 — eine Tiefgarage liegt fast immer im Untergeschoss.
 */
export const geschossOptionen = (ueber: unknown, unter: unknown): string[] => {
  const zahl = (v: unknown, min: number) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), 30) : min;
  };
  const u = zahl(unter, 1);
  // „Über Erdgleiche" zählt das EG mit — 3 Geschosse = EG, OG 1, OG 2.
  const o = Math.max(zahl(ueber, 2) - 1, 1);
  return [
    ...Array.from({ length: u }, (_, i) => `UG ${u - i}`),
    "EG",
    ...Array.from({ length: o }, (_, i) => `OG ${i + 1}`),
  ];
};

/** Beschriftung → Schlüssel. Unbekannte Alt-Beschriftungen liefern `undefined`. */
export const stellplatzArtKey = (label: string): StellplatzArtKey | undefined =>
  STELLPLATZ_ARTEN_DEF.find((a) => a.label === label)?.key;

/** „3 Stellplätze, im Gebäude (Untergeschoss)" — leer, wenn nichts erfasst ist. */
export const stellplatzDetailText = (detail?: StellplatzDetail): string => {
  if (!detail) return "";
  const innen =
    detail.lage === STELLPLATZ_LAGE_INNEN
      ? [
          detail.geschoss,
          detail.flaeche ? `${detail.flaeche} m²` : null,
          detail.ausdehnung,
          detail.flaecheAusserhalb ? `davon außerhalb ${detail.flaecheAusserhalb} m²` : null,
          detail.lageWo,
        ]
          .filter(Boolean)
          .join(", ")
      : "";
  const lage = innen ? `${detail.lage} (${innen})` : detail.lage;
  return [detail.anzahl ? `${detail.anzahl} Stellplätze` : null, lage ?? null]
    .filter(Boolean)
    .join(", ");
};

/**
 * Lesbare Fassung des ganzen Merkmals: „Garage (3 Stellplätze, außerhalb) ·
 * Carport". Unbekannte Alt-Beschriftungen bleiben als reine Beschriftung
 * stehen. Leerer String, wenn nichts ausgewählt ist.
 */
export const stellplatzArtenText = (
  arten: string[],
  details: StellplatzDetails,
): string =>
  arten
    .map((label) => {
      const key = stellplatzArtKey(label);
      const text = key ? stellplatzDetailText(details[key]) : "";
      return text ? `${label} (${text})` : label;
    })
    .join(" · ");
